import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Color, Size, TAG_CATEGORY_ICON_MEANINGS, groupTagsByCategory, isTagCategoryIcon, t, tagFilterCount, toggleFilterCategory, toggleFilterTag,
  type TagCategoryView, type TagFilter, type TagGroup,
} from '@bms2/shared';
import { Button, Checkbox } from '../controls';
import { Icon } from '../Icon';
import { PAGES_PATH, useApi } from '../../pages/records/api';
import styles from './TagFilterMenu.module.css';

/**
 * 🔒 A list's Tags menu (piece 13; T1581, screen E of the tag-categories
 * exploration): the labels grouped by category in the Settings order, the
 * uncategorized last, with *Any* on each category's header. Ticks inside one
 * category mean either; ticks across categories mean all. The Deals list is the
 * first to draw it (the owner's word of 2026-10-07); a list adopts it by naming
 * its record type, because the menu and the server's filter take nothing else.
 */

/** A label the menu offers: the Settings read's row, its category riding along (decision 2260). */
export interface FilterTagOption {
  id: string;
  label: string;
  colour?: string | null;
  category?: TagCategoryView | null;
  isActive?: boolean;
  byRecordType?: Record<string, number>;
}

/**
 * The groups the menu draws: every label in use on this list's record type,
 * plus any the filter already names, so a tick never hides itself.
 */
export function tagFilterGroupsFor(rows: readonly FilterTagOption[], recordType: string, filter: TagFilter): TagGroup<FilterTagOption>[] {
  const offered = rows.filter((row) => row.isActive !== false && ((row.byRecordType?.[recordType] ?? 0) > 0 || filter.tagIds.includes(row.id)));
  return groupTagsByCategory(offered);
}

/** The labels a list can be filtered by, read once from the tags' own route. */
export function useTagFilterOptions(apiBaseUrl: string, enabled: boolean): readonly FilterTagOption[] {
  const read = useApi<{ rows?: FilterTagOption[] }>(enabled ? `${PAGES_PATH}/tags` : null, apiBaseUrl);
  return read.state.kind === 'ready' ? read.state.data.rows ?? [] : [];
}

/**
 * 🔒 The line under the list's title that says what the filter keeps, as screen
 * E writes it: *Equipment: VFD or PLC · and · Region: Any*. Empty for no filter.
 */
export function tagFilterSummary(groups: readonly TagGroup<FilterTagOption>[], filter: TagFilter): string {
  const parts = groups.flatMap((group) => {
    const name = group.category?.name ?? t('tag.noCategory');
    if (group.category && filter.anyCategoryIds.includes(group.category.id)) return [`${name}: ${t('tagFilter.any')}`];
    const ticked = group.tags.filter((tag) => filter.tagIds.includes(tag.id)).map((tag) => tag.label);
    return ticked.length > 0 ? [`${name}: ${ticked.join(` ${t('tagFilter.or')} `)}`] : [];
  });
  return parts.join(` · ${t('tagFilter.and')} · `);
}

export interface TagFilterMenuProps {
  groups: readonly TagGroup<FilterTagOption>[];
  value: TagFilter;
  onChange: (next: TagFilter) => void;
}

export function TagFilterMenu({ groups, value, onChange }: TagFilterMenuProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const count = tagFilterCount(value);
  // The menu closes on a press outside it and on Escape, as a menu does.
  useEffect(() => {
    if (!open) return;
    const away = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', escape);
    };
  }, [open]);
  const label = count > 0 ? `${t('tagFilter.button')} · ${count}` : t('tagFilter.button');
  const anyOf = useMemo(() => new Set(value.anyCategoryIds), [value.anyCategoryIds]);
  return (
    <div ref={ref} className={styles.menu} data-tag-filter={count}>
      <Button size={Size.Small} color={count > 0 ? Color.Navy : Color.Inactive} text={[label]} actions={[() => setOpen((was) => !was)]} pressed={open} />
      {open ? (
        <div className={styles.drop} role="dialog" aria-label={t('tagFilter.label')} data-tag-filter-menu="true">
          {groups.length === 0 ? <p className={styles.empty}>{t('tagFilter.empty')}</p> : null}
          {groups.map((group) => {
            const category = group.category;
            const any = category !== null && anyOf.has(category.id);
            const meaning = TAG_CATEGORY_ICON_MEANINGS[category && isTagCategoryIcon(category.icon) ? category.icon : 'tag'];
            return (
              <section key={category?.id ?? 'none'} className={styles.group} data-tag-filter-group={category?.id ?? 'none'}>
                <div className={styles.head}>
                  <Icon meaning={meaning} size="inline" />
                  <span className={styles.name}>{category?.name ?? t('tag.noCategory')}</span>
                  {/* 🔒 Any on a category's header keeps a record carrying any tag in it (screen E); the uncategorized have no category to name. */}
                  {category ? (
                    <button type="button" className={styles.any} aria-pressed={any} data-tag-filter-any={category.id} onClick={() => onChange(toggleFilterCategory(value, category.id, group.tags.map((tag) => tag.id)))}>
                      {t('tagFilter.any')}
                    </button>
                  ) : null}
                </div>
                <div className={styles.options}>
                  {group.tags.map((tag) => (
                    <span key={tag.id} data-tag-filter-option={tag.id}>
                      <Checkbox label={tag.label} size={Size.Small} checked={any || value.tagIds.includes(tag.id)} disabled={any} onChange={() => onChange(toggleFilterTag(value, tag.id))} />
                    </span>
                  ))}
                </div>
              </section>
            );
          })}
          {count > 0 ? (
            <div className={styles.foot}>
              <button type="button" className={styles.any} data-tag-filter-clear="true" onClick={() => onChange({ tagIds: [], anyCategoryIds: [] })}>
                {t('tagFilter.clear')}
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
